/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { ChevronLeft } from '@zcode-ui/icons'
import { MenuItem } from '@zcode-ui/shadz-ui'
import { selectIsBot } from '@zcode-ui/web-zumper-global-state'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import { TRACK_NAVBAR_ACTION } from '../../constants'
import type { SubmenuType } from '../../types'
import {
  AFRCheapUserSubmenu,
  AFRLuxuryUserSubmenu,
  AFRPetFriendlyUserSubmenu,
  AFRShortTermUserSubmenu,
  AFRUserSubmenu,
  AmenitiesUserSubmenu,
  HFRUserSubmenu,
  MainUserSubmenu,
  RFRUserSubmenu,
} from './submenus'
import type { MainUserSubmenuProps } from './submenus/main/MainUserSubmenu'
import { RentalResourcesSubmenu } from './submenus/resources/RentalResourcesSubmenu'

type MenuListContentProps = {
  activeSubmenu: SubmenuType
  isCurrentSearchView?: boolean
  isDetailView?: boolean
  isHomepageView?: boolean
  isMobile?: boolean
  onLoginClick?: () => void
  onLogoutClick?: () => void
  onSignupClick?: () => void
  setActiveSubmenu: (t: SubmenuType) => void
  showPrevMenu: () => void
  withCityTracking: (trackAction: string) => (name: string, href: string) => void
} & MainUserSubmenuProps

export const MenuListContentBot = (props: MenuListContentProps) => {
  const { activeSubmenu, handleSetActiveSubmenu, showPrevMenu, withCityTracking } =
    props satisfies MenuListContentProps
  const isBot = useSelector(selectIsBot)

  return (
    <div className="hidden" data-testid="header-user-menu">
      <div>
        {activeSubmenu !== 'smMain' && !isBot && (
          <MenuItem
            onSelect={(e) => e.preventDefault()}
            className="flex items-center py-2 px-4 leading-5 mb-1 [&_svg]:size-[unset]"
            onClick={showPrevMenu}
          >
            <ChevronLeft fill={'#726F6C'} size={12} />
            <span className="w-full text-gray-600 cursor-pointer user-select-none hover:text-gray-800">
              Back
            </span>
          </MenuItem>
        )}
        {activeSubmenu !== 'smMain' && isBot && (
          <div
            className="flex items-center py-2 px-4 leading-5 mb-1 [&_svg]:size-[unset]"
            onClick={showPrevMenu}
            onKeyDown={showPrevMenu}
          >
            <ChevronLeft fill={'#726F6C'} size={12} />
            <span className="w-full text-gray-600 cursor-pointer user-select-none hover:text-gray-800">
              Back
            </span>
          </div>
        )}
        <div className={activeSubmenu !== 'smMain' ? 'hidden' : 'block'}>
          <MainUserSubmenu {...props} handleSetActiveSubmenu={handleSetActiveSubmenu} />
        </div>
        <Fragment>
          <div className={activeSubmenu !== 'smAFR' ? 'hidden' : 'block'}>
            <AFRUserSubmenu
              onLinkClick={withCityTracking(TRACK_NAVBAR_ACTION.APARTMENTS_FOR_RENT)}
            />
          </div>

          <div className={activeSubmenu !== 'smHFR' ? 'hidden' : 'block'}>
            <HFRUserSubmenu onLinkClick={withCityTracking(TRACK_NAVBAR_ACTION.HOUSES_FOR_RENT)} />
          </div>

          <div className={activeSubmenu !== 'smRFR' ? 'hidden' : 'block'}>
            <RFRUserSubmenu onLinkClick={withCityTracking(TRACK_NAVBAR_ACTION.ROOMS_FOR_RENT)} />
          </div>

          <div className={activeSubmenu !== 'smAmenities' ? 'hidden' : 'block'}>
            <AmenitiesUserSubmenu handleSetActiveSubmenu={handleSetActiveSubmenu} />
          </div>
          <div className={activeSubmenu !== 'smAFRShortTerm' ? 'hidden' : 'block'}>
            <AFRShortTermUserSubmenu
              onLinkClick={withCityTracking(TRACK_NAVBAR_ACTION.AFR_SHORT_TERM)}
            />
          </div>
          <div className={activeSubmenu !== 'smAFRLuxury' ? 'hidden' : 'block'}>
            <AFRLuxuryUserSubmenu onLinkClick={withCityTracking(TRACK_NAVBAR_ACTION.AFR_LUXURY)} />
          </div>
          <div className={activeSubmenu !== 'smAFRPetFriendly' ? 'hidden' : 'block'}>
            <AFRPetFriendlyUserSubmenu
              onLinkClick={withCityTracking(TRACK_NAVBAR_ACTION.AFR_PET_FRIENDLY)}
            />
          </div>
          <div className={activeSubmenu !== 'smAFRCheap' ? 'hidden' : 'block'}>
            <AFRCheapUserSubmenu onLinkClick={withCityTracking(TRACK_NAVBAR_ACTION.AFR_CHEAP)} />
          </div>
          <div className={activeSubmenu !== 'smResources' ? 'hidden' : 'block'}>
            <RentalResourcesSubmenu />
          </div>
        </Fragment>
      </div>
    </div>
  )
}
